export enum TileAction {
    FetchTile = "FetchTile",
    RetrieveTile = "RetrieveTile",
    StoreTile = "StoreTile",
    ClearTiles = "ClearTiles",
}

export enum TileResult {
    TileFetched = "TileFetched",
    TileRetrieved = "TileRetrieved",
    TileStored = "TileStored",
    TilesCleared = "TilesCleared",
    Error = "Error",
}

// Message Interfaces
export interface FetchTile {
    action: TileAction.FetchTile;
    id: string;
    url: string;
}

export interface StoreTile {
    action: TileAction.StoreTile;
    id: string;
    tileBlob: Blob;
}

export interface RetrieveTile {
    action: TileAction.RetrieveTile;
    id: string;
}

export interface ClearTiles {
    action: TileAction.ClearTiles;
}

export type TileMessage = FetchTile | StoreTile | RetrieveTile | ClearTiles;

// Response Interfaces
export interface TileFetched {
    result: TileResult.TileFetched;
    id: string;
    tileBlob: Blob;
}

export interface TileStored {
    result: TileResult.TileStored;
    id: string;
}

export interface TileRetrieved {
    result: TileResult.TileRetrieved;
    id: string;
    tileBlob: Blob | null;
}

export interface TilesCleared {
    result: TileResult.TilesCleared;
}

export interface Error {
    result: TileResult.Error;
    error: string;
}

export type TileResponse = TileFetched | TileStored | TileRetrieved | TilesCleared | Error;

export type TileActionResponseMap = {
    [TileAction.FetchTile]: TileFetched;
    [TileAction.RetrieveTile]: TileRetrieved;
    [TileAction.StoreTile]: TileStored;
    [TileAction.ClearTiles]: TilesCleared;
};

async function fetchTile(id: string, url: string): Promise<TileFetched> {
    const response = await fetch(url);
    const blob = await response.blob();
    return { result: TileResult.TileFetched, id, tileBlob: blob };
}

async function storeTile(id: string, tileBlob: Blob): Promise<TileStored> {
    const db = await openDatabase();
    const tx = db.transaction('tiles', 'readwrite');
    const store = tx.objectStore('tiles');
    await new Promise<void>((resolve, reject) => {
        const req = store.put({ id, tileBlob });
        req.onsuccess = () => resolve();
        req.onerror = () => reject(new Error(req.error?.message));
    });
    return { result: TileResult.TileStored, id };
}

async function retrieveTile(id: string): Promise<TileRetrieved> {
    const db = await openDatabase();
    const tx = db.transaction('tiles', 'readonly');
    const store = tx.objectStore('tiles');
    const result = await new Promise<any>((resolve, reject) => {
        const req = store.get(id);
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(new Error(req.error?.message));
    });
    return { result: TileResult.TileRetrieved, id, tileBlob: result?.tileBlob || null };
}

async function clearTiles(): Promise<TilesCleared> {
    const db = await openDatabase();
    const tx = db.transaction('tiles', 'readwrite');
    const store = tx.objectStore('tiles');
    await new Promise<void>((resolve, reject) => {
        const req = store.clear();
        req.onsuccess = () => resolve();
        req.onerror = () => reject(new Error(req.error?.message));
    });
    return { result: TileResult.TilesCleared };
}

async function openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open('TileDatabase', 1);
        request.onupgradeneeded = () => {
            const db = request.result;
            if (!db.objectStoreNames.contains('tiles')) {
                db.createObjectStore('tiles', { keyPath: 'id' });
            }
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(new Error(request.error?.message));
    });
}

self.onmessage = async (event: MessageEvent) => {
    const data = event.data as TileMessage;
    try {
        let response: TileResponse;
        switch (data.action) {
            case TileAction.FetchTile:
                response = await fetchTile(data.id, data.url);
                break;
            case TileAction.StoreTile:
                response = await storeTile(data.id, data.tileBlob);
                break;
            case TileAction.RetrieveTile:
                response = await retrieveTile(data.id);
                break;
            case TileAction.ClearTiles:
                response = await clearTiles();
                break;
            default:
                throw new Error(`Unknown action: ${event.data}`);
        }
        self.postMessage(response);
    } catch (error: any) {
        self.postMessage({ result: TileResult.Error, error: error.message } as Error);
    }
};